/** Small pieces shared by the full-screen panels.
 *
 * Every sprite box here is an exact divisor of its source frame - 96px icons at
 * 24 or 48, 128px artifact and spell icons at 32 or 64, 256px portraits at 64 or
 * 256 - so nothing is ever resampled onto a half texel.
 */

import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import type {
  ArmySlot, ArmyState, ContentRegistry, CreatureDefId, Reason,
  ResourceKind, Resources,
} from "@game/core";
import { RESOURCES } from "@game/core";
import { BitmapText } from "../theme/bitmapText.js";
import { PRIMARY_ICON, RESOURCE_ICON } from "../theme/art.js";
import { creatureIcon } from "./art.js";
import { compactCount, costEntries, groupDigits, titleCase } from "./format.js";
import type { CostEntry } from "./format.js";
import { explainReason } from "./townModel.js";
import { MaybeIcon } from "./primitives.js";

export const ICON_SMALL = 24;
export const ICON_LARGE = 48;
export const ART_ICON = 32;
export const ART_ICON_LARGE = 64;
export const PORTRAIT_SMALL = 64;
export const PORTRAIT_LARGE = 256;

// --------------------------------------------------------------- resources

export function ResourceStrip(
  props: { readonly resources: Resources; readonly income?: Resources | null },
): JSX.Element {
  return (
    <div className="hs-cost" role="list" aria-label="Treasury">
      {RESOURCES.map((kind) => (
        <span className="hs-cost__item hs-cost__item--ok" key={kind} role="listitem"
          title={`${titleCase(kind)}: ${groupDigits(props.resources[kind])}`}>
          <MaybeIcon atlasKey={RESOURCE_ICON[kind]} size={ICON_SMALL} title={kind} />
          {groupDigits(props.resources[kind])}
          {props.income != null && props.income[kind] !== 0
            ? <span className="hs-muted hs-tiny">{`+${props.income[kind]}`}</span>
            : null}
        </span>
      ))}
    </div>
  );
}

/**
 * A price as plain words, for somewhere JSX cannot go.
 *
 * `CostRow` is the picture and it is what a player should normally read; this
 * is for a `title` attribute, which holds a string. Both walk `costEntries`, so
 * a price shown one way and spoken the other cannot disagree - which is the
 * whole reason this exists rather than a `${cost.gold} gold` at the call site.
 * Quoting the gold alone is how the upgrade button came to promise a price a
 * player with no crystal could not actually pay - and every upgrade that has a
 * second resource in this content set is a tier-7 one, so it went wrong only
 * where it mattered most.
 */
export function costWords(cost: Partial<Resources> | null | undefined): string {
  const entries = costEntries(cost ?? {});
  if (entries.length === 0) return "nothing";
  const parts = entries.map((e) => `${groupDigits(e.amount)} ${titleCase(e.kind).toLowerCase()}`);
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]!}`;
}

export function CostRow(
  props: { readonly cost: Partial<Resources>; readonly have?: Resources | null },
): JSX.Element {
  const entries: readonly CostEntry[] = costEntries(props.cost);
  if (entries.length === 0) return <span className="hs-muted hs-tiny">Free</span>;
  return (
    <span className="hs-cost">
      {entries.map((e) => {
        const short = props.have != null && props.have[e.kind as ResourceKind] < e.amount;
        return (
          <span key={e.kind}
            className={`hs-cost__item ${short ? "hs-cost__item--short" : "hs-cost__item--ok"}`}
            title={`${groupDigits(e.amount)} ${titleCase(e.kind)}`}>
            <MaybeIcon atlasKey={RESOURCE_ICON[e.kind as ResourceKind]} size={ICON_SMALL} title={e.kind} />
            <BitmapText scale={1}>{groupDigits(e.amount)}</BitmapText>
          </span>
        );
      })}
    </span>
  );
}

// ----------------------------------------------------------------- reasons

/** The kernel's own words for why something is refused. Never a rewrite. */
export function ReasonLine(
  props: { readonly content: ContentRegistry; readonly reason: Reason | null; readonly lead?: string },
): JSX.Element | null {
  if (props.reason === null) return null;
  return (
    <div className="hs-why hs-parch">
      <b>{props.lead ?? "Cannot build:"}</b>{" "}
      {explainReason(props.content, props.reason)}
    </div>
  );
}

// -------------------------------------------------------------- creatures

export interface CreatureSlotProps {
  readonly content: ContentRegistry;
  readonly slot: ArmySlot | null;
  readonly box?: number;
  readonly mark?: string;
  readonly selected?: boolean;
  readonly dragging?: boolean;
  readonly dropState?: "none" | "ok" | "illegal";
  readonly title?: string;
  readonly onClick?: () => void;
  readonly onDragStart?: (e: { shiftKey: boolean }) => void;
  readonly onDrop?: () => void;
  readonly onDragEnter?: () => void;
  readonly children?: ReactNode;
}

/** One army socket. Empty sockets stay in the row: seven is the army's shape. */
export function CreatureSlot(props: CreatureSlotProps): JSX.Element {
  const def = props.slot === null ? null : props.content.creatures.get(props.slot.def) ?? null;
  const box = props.box ?? 56;
  const drop = props.dropState ?? "none";
  const cls = [
    "hs-slot",
    props.slot === null ? "hs-slot--empty" : "",
    props.selected === true ? "hs-slot--drop" : "",
    props.dragging === true ? "hs-slot--dragging" : "",
    drop === "ok" ? "hs-slot--drop" : "",
    drop === "illegal" ? "hs-slot--illegal" : "",
  ].filter((c) => c.length > 0).join(" ");

  const label = def === null
    ? "Empty"
    : `${groupDigits(props.slot?.count ?? 0)} ${def.namePlural}`;

  return (
    <button
      type="button"
      className={cls}
      title={props.title ?? label}
      aria-label={label}
      draggable={props.slot !== null && props.onDragStart !== undefined}
      onDragStart={(e) => props.onDragStart?.({ shiftKey: e.shiftKey })}
      onDragOver={(e) => { if (props.onDrop !== undefined) e.preventDefault(); }}
      onDragEnter={() => props.onDragEnter?.()}
      onDrop={(e) => { e.preventDefault(); props.onDrop?.(); }}
      onClick={props.onClick}
    >
      {def === null ? null : (
        <MaybeIcon atlasKey={creatureIcon(def)} size={box} className="hs-slot__art" title={def.name} />
      )}
      {props.mark === undefined ? null : <span className="hs-slot__mark">{props.mark}</span>}
      {props.slot === null ? null : (
        <span className="hs-slot__count">{compactCount(props.slot.count)}</span>
      )}
      {props.children}
    </button>
  );
}

// ---------------------------------------------------------------- army row

export interface ArmyRowProps {
  readonly content: ContentRegistry;
  readonly army: ArmyState | null;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly dragSource: { readonly army: string; readonly slot: number } | null;
  readonly onDragStart: (armyId: string, slot: number, single: boolean) => void;
  readonly onDrop: (armyId: string, slot: number) => void;
  readonly onSlotClick?: (armyId: string, slot: number) => void;
  readonly box?: number;
  /** Marks the slot the drop would land on, for the highlight. */
  readonly dropTarget?: { readonly army: string; readonly slot: number } | null;
  readonly dropLegal?: boolean;
  readonly onDragEnter?: (armyId: string, slot: number) => void;
}

export function ArmyRow(props: ArmyRowProps): JSX.Element {
  const slots = props.army?.slots ?? [null, null, null, null, null, null, null];
  const id = (props.army?.id ?? "") as string;
  return (
    <div className="hs-armyrow">
      <div className="hs-armyrow__head">
        {props.icon}
        <span className="hs-clip">{props.label}</span>
      </div>
      <div className="hs-armyrow__slots">
        {slots.map((slot, i) => {
          const isTarget = props.dropTarget != null
            && props.dropTarget.army === id && props.dropTarget.slot === i;
          const dropState = !isTarget ? "none" : props.dropLegal === false ? "illegal" : "ok";
          return (
            <CreatureSlot
              key={`${id}:${String(i)}`}
              content={props.content}
              slot={slot}
              box={props.box ?? 56}
              dragging={props.dragSource != null
                && props.dragSource.army === id && props.dragSource.slot === i}
              dropState={dropState}
              onDragStart={(e) => props.onDragStart(id, i, e.shiftKey)}
              onDragEnter={() => props.onDragEnter?.(id, i)}
              onDrop={() => props.onDrop(id, i)}
              onClick={() => props.onSlotClick?.(id, i)}
            />
          );
        })}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- primaries

const PRIMARY_ICON_BY_KEY: Readonly<Record<string, string>> = PRIMARY_ICON;

export function PrimaryStat(
  props: { readonly primary: string; readonly label: string; readonly value: number },
): JSX.Element {
  return (
    <div className="hs-primary hs-stone" title={`${props.label}: ${String(props.value)}`}>
      <MaybeIcon atlasKey={PRIMARY_ICON_BY_KEY[props.primary] ?? null} size={ICON_LARGE} title={props.label} />
      <span className="hs-primary__value">{props.value}</span>
      <span className="hs-primary__label">{props.label}</span>
    </div>
  );
}

// ------------------------------------------------------------------ slider

export interface CountSliderProps {
  readonly value: number;
  readonly max: number;
  readonly onChange: (n: number) => void;
  readonly label: string;
}

/** The recruit slider. Typing in the box and dragging the rail agree, and both
 *  clamp to `max` rather than letting an over-count reach the kernel. */
export function CountSlider(props: CountSliderProps): JSX.Element {
  const clamp = useCallback((n: number): number => {
    if (!Number.isFinite(n)) return 0;
    const v = Math.trunc(n);
    return v < 0 ? 0 : v > props.max ? props.max : v;
  }, [props.max]);

  return (
    <div className="hs-field">
      <div className="hs-armyrow__head">
        <span>{props.label}</span>
        <span className="hs-spacer" />
        {/* `margin-left: auto` rather than the spacer alone: on the hero sheet the
            row head is styled as a flex row whose spacer does not grow, and the
            count sat flush against the label. */}
        <span className="hs-num" style={{ marginLeft: "auto" }}>{`${String(props.value)} / ${String(props.max)}`}</span>
      </div>
      <input
        className="hs-range"
        type="range"
        min={0}
        max={props.max}
        step={1}
        value={props.value}
        aria-label={props.label}
        onChange={(e) => props.onChange(clamp(Number(e.target.value)))}
      />
      <div className="hs-choices">
        <button type="button" className="hs-btn hs-btn--ghost"
          onClick={() => props.onChange(clamp(props.value - 1))}>-1</button>
        <input
          className="hs-input"
          style={{ width: "88px" }}
          inputMode="numeric"
          value={String(props.value)}
          aria-label={`${props.label} count`}
          onChange={(e) => props.onChange(clamp(Number(e.target.value)))}
        />
        <button type="button" className="hs-btn hs-btn--ghost"
          onClick={() => props.onChange(clamp(props.value + 1))}>+1</button>
        <button type="button" className="hs-btn hs-btn--ghost"
          onClick={() => props.onChange(props.max)}>Max</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- helpers

export function useToggle(initial: boolean): readonly [boolean, () => void] {
  const [on, setOn] = useState(initial);
  return [on, useCallback(() => setOn((v) => !v), [])];
}

export const creatureName = (
  content: ContentRegistry, id: CreatureDefId, count: number,
): string => {
  const def = content.creatures.get(id);
  if (def === undefined) return titleCase(id as string);
  return count === 1 ? def.name : def.namePlural;
};
